import React, { useEffect, useState } from 'react';
import { Descriptions, Modal, Spin, Tabs, DatePicker, Table, Input, Tooltip, Tag } from 'antd';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { getShowVehicleDetailsModal, showVehicleDetailsModal } from '../../store/app-ui.store';
import { getSelectedVehicleDetails, getSelectedVehicleDetailsLoading } from '../../store/vehicle.store';
import { getFuelLogs, getFuelSummary } from '../../store/fuel.store';
import FuelSummaryReport from '../Reports/FuelSummaryReport';

const { TabPane } = Tabs;
const { RangePicker } = DatePicker;

const VehicleDetails = (): JSX.Element => {
  const showModal = useAppSelector(getShowVehicleDetailsModal);
  const vehicleDetails = useAppSelector(getSelectedVehicleDetails);
  const loading = useAppSelector(getSelectedVehicleDetailsLoading);

  const [fuelData, setFuelData] = useState<any[]>([]);
  const [dateRange, setDateRange] = useState<[string, string]>(['', '']);
  const [search, setSearch] = useState('');
  /* const [searchText, setSearchText] = useState(""); */

  const dispatch = useAppDispatch();
  const close = () => {
    dispatch(showVehicleDetailsModal(false));
  };

  const filteredFuelData = search
    ? fuelData.filter((row) => (row.date || '').toLowerCase().includes(search.toLowerCase()))
    : fuelData;

  const fuelSummary = useAppSelector(getFuelSummary);

  const fuelColumns = [
    { title: 'Date', dataIndex: 'date' },
    { title: 'Fuel Filled (L)', dataIndex: 'fuelFilled' },
    { title: 'Fuel Used (L)', dataIndex: 'fuelUsed' },
    { title: 'Amount (LKR)', dataIndex: 'amount' },
    { title: 'Odometer (Km)', dataIndex: 'odometer' },
    {
      title: 'Remaining Fuel (L)',
      dataIndex: 'remainingFuel',
      render: (_: number, record: any) => {
        if (record.isFuelInvalid) {
          return (
            <Tooltip title="Fuel used is greater than available fuel.">
              <Tag color="red">Invalid</Tag>
            </Tooltip>
          );
        }
        return record.remainingFuel;
      },
    },
  ];

  useEffect(() => {
    if (!vehicleDetails?.plateNumber) return;
    const payload: any = {
      vehicleId: vehicleDetails.vehicle_id,
      fuelEfficiency: vehicleDetails.fuelEfficiency,
    };

    if (dateRange[0] && dateRange[1]) {
      const [date_from, date_to] = dateRange;
      payload.fromDate = date_from;
      payload.toDate = date_to;
    }
    dispatch(getFuelLogs(payload)).then((response: any) => {
      const logs = response.payload.logs || [];
      setFuelData(logs);
    });
  }, [vehicleDetails?.vehicle_id, dateRange]);

  return (
    <>
      <Modal visible={showModal} onOk={close} destroyOnClose onCancel={close} width={800}>
        <Spin spinning={loading}>
          <h3 style={{ textAlign: 'center', fontWeight: 'bold' }}>Vehicle Details</h3>
          <h3>Vehicle Number : {vehicleDetails?.plateNumber}</h3>
          <Tabs defaultActiveKey="1" centered>
            <TabPane tab="Fuel Summary" key="3">
              <Descriptions
                title={
                  <div>
                    <span>Fuel Summary</span>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '15px',
                        marginTop: '20px',
                        flexWrap: 'wrap',
                      }}
                    >
                      <span style={{ fontWeight: 'normal', fontSize: '14px' }}>Date : </span>
                      <RangePicker
                        onChange={(dates, dateStrings) => setDateRange([dateStrings[0], dateStrings[1]])}
                        style={{ width: 300 }}
                      />
                      <FuelSummaryReport
                        filteredFuelData={filteredFuelData}
                        fuelSummary={fuelSummary}
                        vehicleDetails={vehicleDetails}
                        dateRange={dateRange}
                      />
                    </div>
                  </div>
                }
                column={2}
                bordered
              >
                <Descriptions.Item label="Fuel Type">{vehicleDetails?.fuelType}</Descriptions.Item>
                <Descriptions.Item label="Total Fuel Filled">{fuelSummary?.totalFuelFilled} L</Descriptions.Item>
                <Descriptions.Item label="Total Fuel Consumed">{fuelSummary?.totalFuelConsumed} L</Descriptions.Item>
                <Descriptions.Item label="Total Fuel Cost">{fuelSummary?.totalFuelCost} LKR</Descriptions.Item>
                <Descriptions.Item label="Average Fuel Usage">
                  {fuelSummary?.averageFuelUsage} L/entry
                </Descriptions.Item>
                <Descriptions.Item label="Total Distance Traveled">
                  {fuelSummary?.totalDistanceTraveled} Km
                </Descriptions.Item>
                <Descriptions.Item label="Average Cost per Km">{fuelSummary?.costPerKm} LKR</Descriptions.Item>
                <Descriptions.Item label="Fuel Efficiency">{vehicleDetails?.fuelEfficiency} KM/L</Descriptions.Item>
              </Descriptions>

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '15px',
                  marginTop: '40px',
                  marginBottom: '20px',
                }}
              >
                <span>Search :</span>
                <Input
                  placeholder="Search here"
                  style={{ width: '300px' }}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <span style={{ marginBottom: '20px', fontWeight: 'bold' }}>Fuel History</span>
              <Table
                dataSource={filteredFuelData}
                columns={fuelColumns}
                rowKey="id"
                pagination={{ pageSize: 5 }}
                scroll={{ x: 'max-content' }}
              />
            </TabPane>
          </Tabs>
        </Spin>
      </Modal>
    </>
  );
};

export default VehicleDetails;
